import { API, Card, CoolChoice, Input, Submit, Table } from '@enkla-gui'
import React, { useEffect, useState } from 'react'
import { fasdokcasehelper, notify } from '@/_helpers'
import { useFieldArray, useForm } from 'react-hook-form'

import FasDokModal from './fasdokmodal'
import Modal from 'react-modal'
import Tippy from '@tippyjs/react'
import './signing.scss'

export default function Agreements({ lease }) {
	const { register, handleSubmit, reset } = useForm()
	const signingForm = useForm({
		defaultValues: {
			parties: [{ name: '', email: '', personal_number: '' }],
			message: ''
		}
	})
	const { fields, append, remove: removeParty } = useFieldArray({
		control: signingForm.control,
		name: 'parties'
	})

	const [agreements, setAgreements] = useState([])
	const [error, setError] = useState(false)
	const [sending, setSending] = useState(false)
	const [modal, setModal] = useState(false)
	const [fasDokModal, setFasDokModal] = useState(false)
	const [fileModal, setFileModal] = useState(false)
	const [loading, setLoading] = useState(false)
	const [signingModal, setSigningModal] = useState(false)
	const [selectedAgreement, setSelectedAgreement] = useState(null)

	useEffect(() => {
		if (lease.id === undefined) return
		getAgreements()
	}, [lease])

	const getAgreements = async () => {
		return await API.get(`api/agreement/lease/${lease.id}`)
			.then(res => {
				setAgreements(res)
			})
			.catch(async ([err, res]) => {
				notify.alert('Det gick inte att hämta avtal', res.message, 'error')
			})
	}

	const remove = async agreement => {
		if (
			!(await notify.confirm(
				agreement.fasdokcase !== null
					? 'Hyreskontraktet kommer även att tas bort från Fastighetsägarna Dokument'
					: 'Filen kommer även att tas bort från uthyrningen'
			))
		)
			return
		setLoading(true)
		API.delete(`api/agreement/${agreement.id}`)
			.then(res => {
				notify.toast(`Hyreskontraktet har tagits bort`)
				getAgreements()
			})
			.catch(([err, res]) => {
				notify.toast(`Borttagningen misslyckades: ${res.message}`, 'error')
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const download = agreement => {
		if (agreement.fasdokcase !== null) {
			downloadFasDokPDF(agreement.fasdokcase.case_id)
		} else {
			downloadFile(agreement.file_id)
		}
	}

	const downloadFile = id => {
		API.get(`api/file/${id}`)
			.then(res => {
				var link = document.createElement('a')
				link.href = res
				link.target = '_blank'
				link.click()
			})
			.catch(([err, res]) => {
				notify.toast(res.message, 'error')
				setSending(false)
				setError({
					message: res.message,
					data: res.data
				})
			})
	}

	const downloadFasDokPDF = case_id => {
		API.get(`api/integration/fasdok/${case_id}/pdf`)
			.then(res => {
				const linkSource = `data:application/pdf;base64,${res.file}`
				const downloadLink = document.createElement('a')
				const fileName = `Avtal_${case_id}.pdf`

				downloadLink.href = linkSource
				downloadLink.download = fileName
				downloadLink.click()
			})
			.catch(([err, res]) => {
				notify.toast(res.message, 'error')
			})
	}

	const create = (file, name) => {
		API.post('api/agreement', {
			name: name,
			file_id: file.id,
			lease_id: lease.id
		})
			.then(res => {
				notify.toast('Hyreskontraktet har skapts')
				reset()
				setFileModal(false)
				getAgreements()
			})
			.catch(([err, res]) => {
				notify.toast(res.message, 'error')
			})
			.finally(() => {
				setSending(false)
			})
	}

	const upload = data => {
		setSending(true)
		var formData = new FormData()
		formData.append('file', data.file[0])
		formData.append('name', data.alias)
		formData.append('fileable_id', lease.id)
		formData.append('fileable_type', 'lease')

		API.upload('api/file', formData)
			.then(res => {
				create(res.file, data.name)
			})
			.catch(([err, res]) => {
				notify.toast(res.message, 'error')
				setError({
					message: res.message,
					data: res.data
				})
				setSending(false)
			})
	}

	const isPdf = file => file?.mimetype ? file.mimetype.toLowerCase() === 'application/pdf' : /\.pdf$/i.test(file?.name || '')
	const pdfError = 'Endast PDF-filer kan skickas för signering. Ladda upp avtalet som en PDF och försök igen.'

	const openSigningModal = (agreement) => {
		setError(isPdf(agreement.file) ? false : { message: pdfError })
		setSelectedAgreement(agreement)

		// Auto-fill parties from lease tenants
		if (lease && lease.tenants && lease.tenants.length > 0) {
			const parties = lease.tenants.map(tenant => ({
				name: tenant.name || '',
				email: tenant.email || '',
				personal_number: tenant.identifier || ''
			}))
			signingForm.setValue('parties', parties)
		} else {
			signingForm.setValue('parties', [{ name: '', email: '', personal_number: '' }])
		}

		signingForm.setValue('message', '')
		setSigningModal(true)
	}

	const addSigningParty = () => {
		append({ name: '', email: '', personal_number: '' })
	}

	const removeSigningParty = (index) => {
		removeParty(index)
	}

	const handleSigningSubmit = async () => {
		if (!isPdf(selectedAgreement?.file)) {
			setError({ message: pdfError })
			return
		}
		setError(false)
		setSending(true)
		try {
			await signingForm.handleSubmit(sendForSigning)()
		} catch (error) {
			setSending(false)
		}
	}

	const sendForSigning = async (data) => {
		API.post(`api/zigned/agreement/${selectedAgreement.id}/sign`, {
			parties: data.parties,
			message: data.message
		})
			.then(res => {
				notify.toast(res.message)
				setSigningModal(false)
				signingForm.reset({
					parties: [{ name: '', email: '', personal_number: '' }],
					message: ''
				})
				getAgreements()
				setSending(false)
			})
			.catch(([err, res]) => {
				setError({
					message: res.message,
					data: res.data
				})
				setSending(false)
			})
	}

	const getSigningStatusBadge = (agreement) => {
		if (!agreement.signing_status) return null

		const statusConfig = {
			draft: { text: 'Utkast', bg: 'secondary', icon: 'fas fa-edit' },
			pending: { text: 'Väntar på signatur', bg: 'warning', icon: 'fas fa-clock' },
			completed: { text: 'Signerad', bg: 'success', icon: 'fas fa-check-circle' },
			signed: { text: 'Signerad', bg: 'success', icon: 'fas fa-check-circle' },
			cancelled: { text: 'Avbruten', bg: 'danger', icon: 'fas fa-times-circle' },
			expired: { text: 'Utgången', bg: 'secondary', icon: 'fas fa-calendar-times' },
			rejected: { text: 'Avvisad', bg: 'danger', icon: 'fas fa-times-circle' },
			unknown: { text: 'Väntar på information', bg: 'info', icon: 'fas fa-signature' }
		}

		const config = statusConfig[agreement.signing_status] || { text: agreement.signing_status, bg: 'info', icon: 'fas fa-signature' }

		return (
			<span className={`badge bg-${config.bg} text-white ms-2`}>
				<i className={`${config.icon} me-1`} />
				{config.text}
			</span>
		)
	}

	return (
		<>
			<Card
				loading={!agreements || loading}
				title='Hyreskontrakt'
				subtitle='Signeringsstatus uppdateras automatiskt från Zigned'
				icon='fas fa-fw fa-gavel'
				collapsed={agreements?.length === 0}
				buttons={{
					header: [
						{
							icon: 'fas fa-plus-square', text: 'Skapa', callback: () => setModal(true),
							hidden: lease?.state?.status === 'inactive'
						},
					]
				}}>
				<Table
					rows={agreements}
					empty={{ text: 'Det finns inte några hyreskontrakt', icon: 'fas fa-gavel' }}
					functions={{
						remove: agreement => remove(agreement),
						download: agreement => download(agreement),
						sign: agreement => openSigningModal(agreement),
						signed: signed => <span class={`badge bg-${signed ? 'success' : 'danger'}`}>{signed ? 'Signerat' : 'Ej signerat'}</span>,
						type: agreement => <span class='badge bg-default'>{agreement.fasdokcase !== null ? 'Fastighetsägarna' : 'Fil'}</span>,
						status: agreement =>
							agreement.fasdokcase === null ? (
								getSigningStatusBadge(agreement) || <span className={`badge bg-info text-white ms-2`}>
									<i className={`fas fa-signature me-1`} />
									Ej signerat
								</span>
							) : (
								<span class={`badge w-100 bg-${fasdokcasehelper.status(agreement.fasdokcase.status).color}`}>
									{fasdokcasehelper.status(agreement.fasdokcase.status).label}
								</span>
							),
						reference: agreement =>
							agreement.fasdokcase !== null ? (
								<a target='_blank' class='link' href={`https://app.fastdok.se/contract-cases/${agreement.fasdokcase.case_id}`} rel="noreferrer">
									{agreement.fasdokcase.case_id}
								</a>
							) : (
								agreement.file?.name ?? '-'
							),
						buttons: agreement => {
							const buttons = []
							// Only show sign button for file-based agreements
							if (agreement.fasdokcase === null && (!agreement.signing_status || agreement.signing_status === 'rejected')) {
								buttons.push(<Tippy content='Skicka för e-signering'><button class='btn btn-sm' onClick={() => openSigningModal(agreement)} title='Skicka för signering'><i class='fas fa-signature' /></button></Tippy>)
							}
							buttons.push(<button class='btn btn-sm' onClick={() => download(agreement)} title='Ladda ner'><i class='fas fa-file-download' /></button>)
							buttons.push(<button class='btn btn-sm' onClick={() => remove(agreement)} title='Ta bort'><i class='fas fa-trash-alt' /></button>)
							return <>{buttons}</>
						}
					}}
					keys={[
						'name',
						'_this>function:type',
						'_this>function:reference',
						'_this>function:status',
						'created_at>date:full',
						'_this>function:buttons'
					]}>
					<th>Namn</th>
					<th class='min'>Typ</th>
					<th>Referens</th>
					<th class='min'>Status</th>
					<th>Skapad</th>
					<th class='min' />
				</Table>
			</Card>
			<FasDokModal
				id={lease.id}
				close={update => {
					setFasDokModal(false)
					if (update) getAgreements()
				}}
				isOpen={fasDokModal}
			/>
			<Modal isOpen={modal} onRequestClose={() => setModal(false)} className='no-padding'>
				<Card
					collapsed={null}
					title='Skapa ett hyreskontrakt'
					icon='fas fa-fw fa-gavel'
					buttons={{
						header: [{ callback: () => setModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }]
					}}>
					<div class='row mb-3'>
						<div class='col'>
							<CoolChoice
								callback={() => {
									setModal(false)
									setFasDokModal(true)
								}}
								icon='fas fa-handshake'
								title='Fastighetsägarna Dokument'
								description='Har du valt att integrera Fastighetsägarna Dokument så kan du skapa upp ett hyreskontrakt med hjälp av deras mallar.'
							/>
						</div>
					</div>
					<div class='row'>
						<div class='col'>
							<CoolChoice
								callback={() => {
									setModal(false)
									setFileModal(true)
								}}
								icon='fas fa-file-pdf'
								title='Ladda upp som fil'
								description='Har du ditt hyreskontrakt som fil så kan du välja att ladda upp den'
							/>
						</div>
					</div>
				</Card>
			</Modal>
			<Modal isOpen={fileModal} onRequestClose={() => setFileModal(false)} className='no-padding'>
				<Card
					collapsed={null}
					title='Ladda upp fil'
					icon='fas fa-fw fa-upload'
					buttons={{
						header: [{ callback: () => setFileModal(false), icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
						footer: [<Submit onSubmit={handleSubmit(upload)} text='Ladda upp' sending={sending} />]
					}}>
					<div class='mb-3 form-group'>
						<label>Fil</label>
						<input {...register('file', { required: true })} class='form-control' type='file' name='file' />
						{error?.data?.file && (
							<div class='form-inline-error'>
								<i class='fa fa-fw fa-exclamation-triangle' /> {error.data.file.map(err => `${err}, `)}{' '}
							</div>
						)}
					</div>
					<div class='row mb-3'>
						<Input controls={{ register }} name='name' options={{ required: true }} label='Namn' error={error} />
					</div>
				</Card>
			</Modal>
			{/* Signing Modal */}
			<Modal isOpen={signingModal} onRequestClose={() => { setSigningModal(false); setError(false)}} className='no-padding bigboy signing-modal'>
				<Card
					title='Skicka för signering'
					collapsed={null}
					icon='fas fa-fw fa-signature'
					buttons={{
						header: [{ callback: () => { setSigningModal(false); setError(false) }, icon: 'fas fa-times', text: 'Avbryt', bg: 'light' }],
						footer: [<Submit onSubmit={handleSigningSubmit} text='Skicka för signering' sending={sending} disabled={!isPdf(selectedAgreement?.file)} />]
					}}>
					{error?.message && (
						<div className='signing-modal__error' role='alert'>
							<i className='fas fa-exclamation-circle' aria-hidden='true' />
							<div><strong>Det gick inte att skicka för signering</strong><p>{error.message}</p></div>
						</div>
					)}
					<div className='mb-3'>
						<div className='signing-modal__file'><i className='far fa-file-pdf' aria-hidden='true' /><span>{selectedAgreement?.file?.name}</span></div>
					</div>

					<div className='mb-3'>
						<h6 className='mb-3'>Signeringsparter</h6>
						<p className='signing-modal__description'>Varje part får ett e-postmeddelande med en länk till avtalet för signering.</p>
						{fields.map((field, index) => (
							<div key={field.id} className='signing-modal__party'>
								<div className='signing-modal__party-body'>
									<div className='signing-modal__fields'>
										<Input
											error={error}
											controls={signingForm}
											name={`parties.${index}.name`}
											label='Namn *'
											placeholder='För- och efternamn'
											cols='signing-modal__field'
										/>
										<Input
											error={error}
											controls={signingForm}
											name={`parties.${index}.email`}
											label='E-postadress *'
											type='email'
											placeholder='exempel@domän.se'
											cols='signing-modal__field'
										/>
										<Input
											error={error}
											controls={signingForm}
											name={`parties.${index}.personal_number`}
											label='Personnummer'
											placeholder='ÅÅÅÅMMDD-XXXX'
											cols='signing-modal__field'
										/>
										<div className='signing-modal__remove'>
											{fields.length > 1 && (
											<Tippy content='Ta bort part'>
												<button type='button' className='btn btn-sm' aria-label={`Ta bort part ${index + 1}`} onClick={() => removeSigningParty(index)}>
													<i className='fas fa-times' />
												</button>
											</Tippy>
											)}
										</div>
									</div>
								</div>
							</div>
						))}
						<button
							type='button'
							className='btn btn-sm btn-outline-primary'
							onClick={addSigningParty}
						>
							<i className='fas fa-plus me-2' />
							Lägg till part
						</button>
					</div>
				</Card>
			</Modal>
		</>
	)
}
